import { useUser } from "@clerk/clerk-react";
import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { AlertCircle, Clock, Loader2, Mail, Send, UserCheck, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import type { BadgeTone } from "@/components/admin/StatusBadge";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useClaimSupportRequest,
  useEditSupportReply,
  useReplyToSupportRequest,
  useSupportRequests,
  useSupportSettings,
  useUpdateSupportRequest,
  useUpdateSupportSettings,
} from "@/hooks/admin/support";
import { AdminLayout } from "@/layouts/AdminLayout";
import { isSuperadminRole } from "@/lib/roles";
import type {
  SupportRequestAdminItem,
  SupportRequestResolution,
  SupportRequestSeverity,
  SupportRequestStatus,
  SupportRequestType,
} from "@/types";

const PAGE_SIZE = 25;

// Backend sortable keys for the support list; restrict TanStack sorting to these.
const SORTABLE = new Set([
  "subject",
  "type",
  "status",
  "severity",
  "owner_email",
  "user_username",
  "requester_email",
  "requester_name",
  "intake_channel",
  "created_at",
  "updated_at",
]);

const STATUS_OPTIONS: { value: SupportRequestStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
];

const TYPE_LABELS: Record<SupportRequestType, string> = {
  question: "Question",
  feature_request: "Feature request",
  other: "Other",
};

const STATUS_TONE: Record<SupportRequestStatus, BadgeTone> = {
  new: "info",
  in_progress: "warning",
  resolved: "success",
};

const TYPE_TONE: Record<SupportRequestType, BadgeTone> = {
  question: "info",
  feature_request: "neutral",
  other: "neutral",
};

const SEVERITY_OPTIONS: { value: SupportRequestSeverity; label: string }[] = [
  { value: "blocking", label: "Blocking — send to dev" },
  { value: "standard", label: "Standard" },
  { value: "minor", label: "Minor" },
];

const SEVERITY_LABELS: Record<SupportRequestSeverity, string> = {
  blocking: "Blocking",
  standard: "Standard",
  minor: "Minor",
};

const SEVERITY_TONE: Record<SupportRequestSeverity, BadgeTone> = {
  blocking: "danger",
  standard: "warning",
  minor: "neutral",
};

// The three ways a request can end. Recorded apart from status, which says
// "resolved" for all of them — so the queue can show how many were answered
// against how many were parked.
const RESOLUTION_OPTIONS: { value: SupportRequestResolution; label: string }[] = [
  { value: "answered", label: "Answered — fixed on the spot" },
  { value: "backlogged", label: "Logged for later" },
  { value: "escalated", label: "Sent to dev" },
];

const RESOLUTION_LABELS: Record<SupportRequestResolution, string> = {
  answered: "Answered",
  backlogged: "Logged",
  escalated: "Sent to dev",
};

const INTAKE_LABELS = { in_app: "In app", email: "Email" } as const;

const NO_RESOLUTION = "NONE";

/**
 * How long an unclaimed request has before it is handed on, in words.
 *
 * Working hours, so the wall-clock gap is not the answer — this reports the
 * remaining calendar time to the deadline the server computed, which is the
 * number someone deciding whether to pick it up actually wants.
 */
function formatTimeLeft(dueAt: string): { label: string; overdue: boolean } {
  const remainingMs = new Date(dueAt).getTime() - Date.now();
  if (remainingMs <= 0) return { label: "Due now", overdue: true };
  const hours = Math.floor(remainingMs / 3_600_000);
  if (hours >= 24) return { label: `${Math.floor(hours / 24)}d left`, overdue: false };
  if (hours >= 1) return { label: `${hours}h left`, overdue: false };
  return { label: `${Math.max(1, Math.round(remainingMs / 60_000))}m left`, overdue: false };
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface DetailModalProps {
  entry: SupportRequestAdminItem | null;
  onClose: () => void;
}

function DetailModal({ entry, onClose }: DetailModalProps) {
  const [status, setStatus] = useState<SupportRequestStatus>("new");
  const [severity, setSeverity] = useState<SupportRequestSeverity>("standard");
  const [resolution, setResolution] = useState<SupportRequestResolution | typeof NO_RESOLUTION>(
    NO_RESOLUTION
  );
  const [linkedIssue, setLinkedIssue] = useState("");
  const [notes, setNotes] = useState("");
  const [replyText, setReplyText] = useState("");
  const [editingReply, setEditingReply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: updateSupportRequest, isPending: saving } = useUpdateSupportRequest();
  const { mutateAsync: sendReply, isPending: sendingReply } = useReplyToSupportRequest();
  const { mutateAsync: editReply, isPending: editingReplyPending } = useEditSupportReply();
  const { mutateAsync: claimRequest, isPending: claiming } = useClaimSupportRequest();

  const { user } = useUser();
  const isSuperadmin = isSuperadminRole(user?.publicMetadata?.role as string | undefined);

  useEffect(() => {
    if (entry) {
      setStatus(entry.status);
      setSeverity(entry.severity);
      setResolution(entry.resolution ?? NO_RESOLUTION);
      setLinkedIssue(entry.linked_issue ?? "");
      setNotes(entry.admin_notes ?? "");
      setReplyText("");
      setEditingReply(false);
      setError(null);
    }
  }, [entry]);

  if (!entry) return null;

  const dirty =
    status !== entry.status ||
    severity !== entry.severity ||
    resolution !== (entry.resolution ?? NO_RESOLUTION) ||
    linkedIssue !== (entry.linked_issue ?? "") ||
    notes !== (entry.admin_notes ?? "");
  const replyBusy = sendingReply || editingReplyPending;
  const timeLeft = entry.handover_due_at ? formatTimeLeft(entry.handover_due_at) : null;

  const handleSave = async () => {
    setError(null);
    try {
      await updateSupportRequest({
        id: entry.id,
        patch: {
          status,
          severity,
          admin_notes: notes,
          linked_issue: linkedIssue.trim() || null,
          // Only send a resolution when one is chosen — the API treats it as
          // closing the request, so an unset value must stay unset.
          ...(resolution === NO_RESOLUTION ? {} : { resolution }),
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update");
    }
  };

  const handleClaim = async () => {
    setError(null);
    try {
      await claimRequest({ id: entry.id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to claim");
    }
  };

  const handleSendReply = async () => {
    setError(null);
    try {
      await sendReply({ id: entry.id, reply_body: replyText.trim() });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reply");
    }
  };

  const handleEditReply = async () => {
    setError(null);
    try {
      await editReply({ id: entry.id, reply_body: replyText.trim() });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update reply");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Support Request</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          {/* Ownership first: the question this queue exists to answer is not
              "what is new" but "who has this". */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <UserCheck className="h-4 w-4 flex-shrink-0 text-gray-400" />
              {entry.owner_email ? (
                <span className="text-gray-900">
                  Owned by <span className="font-medium">{entry.owner_email}</span>
                  {entry.claimed_at ? (
                    <span className="text-gray-500"> since {formatDate(entry.claimed_at)}</span>
                  ) : null}
                </span>
              ) : (
                <span className="text-gray-600">Nobody has taken this yet</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {timeLeft && (
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium ${
                    timeLeft.overdue ? "text-red-600" : "text-gray-500"
                  }`}
                >
                  <Clock className="h-3.5 w-3.5" />
                  {timeLeft.label}
                </span>
              )}
              {entry.escalated_at && <StatusBadge tone="danger">Handed over</StatusBadge>}
              {!entry.owner_email && (
                <button
                  type="button"
                  onClick={handleClaim}
                  disabled={claiming}
                  className="inline-flex items-center gap-2 rounded bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {claiming && <Loader2 className="h-4 w-4 animate-spin" />}
                  I&rsquo;ll take this
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-xs font-medium text-gray-500">Sender</span>
              <p className="mt-1 text-gray-900">
                {entry.requester_name || entry.user_username || entry.requester_email || "Unknown"}
              </p>
              {entry.requester_email && (
                <p className="text-xs text-gray-500">{entry.requester_email}</p>
              )}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Intake channel</span>
              <p className="mt-1">
                <StatusBadge tone={entry.intake_channel === "email" ? "info" : "neutral"}>
                  {INTAKE_LABELS[entry.intake_channel]}
                </StatusBadge>
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Type</span>
              <p className="mt-1">
                <StatusBadge tone={TYPE_TONE[entry.type]}>{TYPE_LABELS[entry.type]}</StatusBadge>
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Created</span>
              <p className="mt-1 text-gray-900">{formatDate(entry.created_at)}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Last updated</span>
              <p className="mt-1 text-gray-900">{formatDate(entry.updated_at)}</p>
            </div>
          </div>

          <div>
            <span className="text-xs font-medium text-gray-500">Subject</span>
            <p className="mt-1 font-medium text-gray-900">{entry.subject}</p>
          </div>

          <div>
            <span className="text-xs font-medium text-gray-500">Message</span>
            <div className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-3 text-sm text-gray-900">
              {entry.message}
            </div>
          </div>

          {/* Reply — user-visible, emailed to the requester. Distinct from the
              internal Admin notes field below. One-shot: locked after sending,
              editable only by a superadmin. */}
          <div>
            <span className="text-xs font-medium text-gray-500">Reply to user</span>
            {entry.reply_body && !editingReply ? (
              <div className="mt-1 rounded border border-green-200 bg-green-50 p-3">
                <p className="whitespace-pre-wrap text-sm text-gray-900">{entry.reply_body}</p>
                <p className="mt-2 text-xs text-gray-500">
                  Sent{entry.replied_at ? ` on ${formatDate(entry.replied_at)}` : ""}
                </p>
                {isSuperadmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setReplyText(entry.reply_body ?? "");
                      setEditingReply(true);
                    }}
                    className="mt-2 text-xs font-medium text-primary hover:underline"
                  >
                    Edit reply
                  </button>
                )}
              </div>
            ) : (
              <>
                <Textarea
                  id="support-reply"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                  placeholder={
                    entry.reply_body
                      ? "Correct the reply the user sees"
                      : "Write a reply — emailed to the user and marks the request resolved"
                  }
                  className="mt-1"
                  disabled={replyBusy}
                />
                <div className="mt-2 flex justify-end gap-2">
                  {editingReply && (
                    <button
                      type="button"
                      onClick={() => setEditingReply(false)}
                      disabled={replyBusy}
                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={entry.reply_body ? handleEditReply : handleSendReply}
                    disabled={replyBusy || replyText.trim().length === 0}
                    className="inline-flex items-center gap-2 rounded bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {replyBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {entry.reply_body ? "Save reply" : "Send reply"}
                  </button>
                </div>
              </>
            )}
          </div>

          <div>
            <label htmlFor="support-status" className="text-xs font-medium text-gray-500">
              Status
            </label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as SupportRequestStatus)}
              disabled={saving}
            >
              <SelectTrigger id="support-status" className="mt-1 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="support-severity" className="text-xs font-medium text-gray-500">
                Severity
              </label>
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as SupportRequestSeverity)}
                disabled={saving}
              >
                <SelectTrigger id="support-severity" className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="support-resolution" className="text-xs font-medium text-gray-500">
                Outcome
              </label>
              <Select
                value={resolution}
                onValueChange={(v) => setResolution(v as SupportRequestResolution)}
                disabled={saving}
              >
                <SelectTrigger id="support-resolution" className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_RESOLUTION}>Not decided yet</SelectItem>
                  {RESOLUTION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-gray-500">
                {resolution === "escalated"
                  ? "Emails the dev contact with this request and closes it."
                  : "Choosing an outcome closes the request."}
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="support-linked-issue" className="text-xs font-medium text-gray-500">
              Linked issue
            </label>
            <Input
              id="support-linked-issue"
              type="text"
              value={linkedIssue}
              onChange={(e) => setLinkedIssue(e.target.value)}
              placeholder="KC-000 or a Linear URL — optional"
              disabled={saving}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-gray-500">
              Without one, a request logged for later leaves no trail outside this queue.
            </p>
          </div>

          <div>
            <label htmlFor="support-notes" className="text-xs font-medium text-gray-500">
              Admin notes
            </label>
            <Textarea
              id="support-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Internal notes about this request"
              className="mt-1"
              disabled={saving}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Who gets told about support requests. Superadmin-only, because a wrong address
 * here fails silently — mail goes somewhere nobody reads, which is the exact
 * failure the rest of this page exists to prevent.
 */
function RecipientsPanel({ onClose }: { onClose: () => void }) {
  const { data, isPending } = useSupportSettings();
  const { mutateAsync: save, isPending: saving } = useUpdateSupportSettings();
  const [notify, setNotify] = useState("");
  const [escalation, setEscalation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (data && !loaded) {
      setNotify(data.notify_emails.join("\n"));
      setEscalation(data.escalation_email);
      setLoaded(true);
    }
  }, [data, loaded]);

  const handleSave = async () => {
    setError(null);
    try {
      await save({
        notify_emails: notify
          .split(/[\n,]/)
          .map((a) => a.trim())
          .filter(Boolean),
        escalation_email: escalation.trim(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Support recipients</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          {isPending ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="support-notify" className="text-xs font-medium text-gray-500">
                  Alerted on every new request
                </label>
                <Textarea
                  id="support-notify"
                  value={notify}
                  onChange={(e) => setNotify(e.target.value)}
                  rows={4}
                  placeholder={"jonty@example.com\nmatus@example.com"}
                  className="mt-1 font-mono text-sm"
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-gray-500">
                  One address per line. Everyone here is told at once, and each gets their own claim
                  link. Leave empty to switch the alert off entirely.
                </p>
              </div>

              <div>
                <label htmlFor="support-escalation" className="text-xs font-medium text-gray-500">
                  Takes unclaimed requests, and anything sent to dev
                </label>
                <Input
                  id="support-escalation"
                  type="email"
                  value={escalation}
                  onChange={(e) => setEscalation(e.target.value)}
                  placeholder="matus@example.com"
                  className="mt-1"
                  disabled={saving}
                />
              </div>

              {data?.updated_at && (
                <p className="text-xs text-gray-500">Last changed {formatDate(data.updated_at)}</p>
              )}

              {error && (
                <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || isPending}
            className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSupportPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SupportRequestStatus | "ALL">("ALL");
  const [severityFilter, setSeverityFilter] = useState<SupportRequestSeverity | "ALL">("ALL");
  const [unownedOnly, setUnownedOnly] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);
  const [selected, setSelected] = useState<SupportRequestAdminItem | null>(null);
  const [showRecipients, setShowRecipients] = useState(false);
  const { user } = useUser();
  const isSuperadmin = isSuperadminRole(user?.publicMetadata?.role as string | undefined);

  const sort = sorting[0];
  const supportQuery = useSupportRequests({
    page,
    page_size: PAGE_SIZE,
    sort_by: sort?.id ?? "created_at",
    sort_order: sort ? (sort.desc ? "desc" : "asc") : "desc",
    q: search || undefined,
    status: statusFilter === "ALL" ? undefined : statusFilter,
    severity: severityFilter === "ALL" ? undefined : severityFilter,
    unowned: unownedOnly || undefined,
  });
  const data = supportQuery.data ?? null;
  const error = supportQuery.isError
    ? supportQuery.error instanceof Error
      ? supportQuery.error.message
      : "Failed to load support requests"
    : null;

  const columns = useMemo<ColumnDef<SupportRequestAdminItem>[]>(
    () => [
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">{formatDate(row.original.created_at)}</span>
        ),
      },
      {
        accessorKey: "requester_name",
        header: "Sender",
        cell: ({ row }) => (
          <div>
            <span className="font-medium text-gray-900">
              {row.original.requester_name ||
                row.original.user_username ||
                row.original.requester_email ||
                "Unknown"}
            </span>
            {row.original.requester_email && (
              <span className="block text-xs text-gray-500">{row.original.requester_email}</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "intake_channel",
        header: "Channel",
        cell: ({ row }) => (
          <StatusBadge tone={row.original.intake_channel === "email" ? "info" : "neutral"}>
            {INTAKE_LABELS[row.original.intake_channel]}
          </StatusBadge>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <StatusBadge tone={TYPE_TONE[row.original.type]}>
            {TYPE_LABELS[row.original.type]}
          </StatusBadge>
        ),
      },
      {
        accessorKey: "subject",
        header: "Subject",
        cell: ({ row }) => (
          <span className="block max-w-md truncate text-sm text-gray-900">
            {row.original.subject}
          </span>
        ),
      },
      {
        accessorKey: "severity",
        header: "Severity",
        cell: ({ row }) => (
          <StatusBadge tone={SEVERITY_TONE[row.original.severity]}>
            {SEVERITY_LABELS[row.original.severity]}
          </StatusBadge>
        ),
      },
      {
        accessorKey: "owner_email",
        header: "Owner",
        cell: ({ row }) => {
          const { owner_email, handover_due_at, escalated_at } = row.original;
          if (owner_email) {
            return <span className="text-sm text-gray-900">{owner_email}</span>;
          }
          // Unowned is the state worth seeing at a glance, so it carries the
          // countdown rather than sitting as an empty cell.
          const timeLeft = handover_due_at ? formatTimeLeft(handover_due_at) : null;
          return (
            <span
              className={`inline-flex items-center gap-1 text-sm ${
                escalated_at || timeLeft?.overdue ? "text-red-600" : "text-gray-500"
              }`}
            >
              <Clock className="h-3.5 w-3.5" />
              {escalated_at ? "Handed over" : (timeLeft?.label ?? "Unclaimed")}
            </span>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const { status, resolution } = row.original;
          return (
            <div className="flex items-center gap-1.5">
              <StatusBadge tone={STATUS_TONE[status]}>{status.replace("_", " ")}</StatusBadge>
              {resolution && (
                <span className="text-xs text-gray-500">{RESOLUTION_LABELS[resolution]}</span>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const valid = next.filter((s) => SORTABLE.has(s.id));
      setPage(1);
      return valid.length ? valid : prev;
    });
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={supportQuery.isPending}
          error={error}
          onRetry={() => supportQuery.refetch()}
          emptyMessage="No support requests match the current filters"
          getRowId={(r) => r.id}
          onRowClick={(r) => setSelected(r)}
          toolbar={
            <div className="flex items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                placeholder="Search by sender, subject, or message…"
              />
              <div className="flex items-center gap-3">
                <label
                  htmlFor="support-unowned-only"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-gray-700"
                >
                  <Checkbox
                    id="support-unowned-only"
                    checked={unownedOnly}
                    onCheckedChange={(checked) => {
                      setUnownedOnly(checked === true);
                      setPage(1);
                    }}
                  />
                  Unowned only
                </label>
                <Select
                  value={severityFilter}
                  onValueChange={(v) => {
                    setSeverityFilter(v as SupportRequestSeverity | "ALL");
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All severities</SelectItem>
                    {SEVERITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {SEVERITY_LABELS[opt.value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v as SupportRequestStatus | "ALL");
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All statuses</SelectItem>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isSuperadmin && (
                  <button
                    type="button"
                    onClick={() => setShowRecipients(true)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Mail className="h-4 w-4" />
                    Recipients
                  </button>
                )}
              </div>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>

      <DetailModal entry={selected} onClose={() => setSelected(null)} />
      {showRecipients && <RecipientsPanel onClose={() => setShowRecipients(false)} />}
    </AdminLayout>
  );
}
